import React, { useState } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { 
  EditIcon,
  TrashIcon
} from "lucide-react";
import PaginatedTable, { PaginatedTableColumn } from "../../ui/paginated-table";

interface Choice {
  id: number;
  value: string;
}

interface ChoicesTableProps {
  choices: string[];
  onChoiceChange: (index: number, value: string) => void;
  onRemoveChoice: (index: number) => void;
  emptyTitle?: string;
  emptyDescription?: string;
}

const ChoicesTable: React.FC<ChoicesTableProps> = ({
  choices,
  onChoiceChange,
  onRemoveChoice,
  emptyTitle = "No choices added",
  emptyDescription = "Add at least two choices to enable multiple choice corrections."
}) => {
  const [editingIndex, setEditingIndex] = useState<number>(-1);
  const [editingValue, setEditingValue] = useState<string>("");

  const startEditing = (index: number) => {
    setEditingIndex(index);
    setEditingValue(choices[index] || "");
  };

  const saveEdit = () => {
    if (editingIndex >= 0) {
      onChoiceChange(editingIndex, editingValue);
    }
    setEditingIndex(-1);
    setEditingValue("");
  };

  const cancelEdit = () => {
    setEditingIndex(-1);
    setEditingValue("");
  };

  // Convert choices array to data format for PaginatedTable
  const tableData: Choice[] = choices.map((choice, index) => ({
    id: index,
    value: choice
  }));

  const columns: PaginatedTableColumn<Choice>[] = [
    {
      key: "value",
      header: "Choice",
      width: "w-[70%]",
      render: (choice) => (
        editingIndex === choice.id ? (
          <Input
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            placeholder="Enter choice"
            className="w-full"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                saveEdit();
              } else if (e.key === "Escape") {
                cancelEdit();
              }
            }}
          />
        ) : (
          <div className="text-sm font-gc-reg text-foreground">
            {choice.value || <span className="text-muted-foreground">Empty choice</span>}
          </div>
        )
      )
    },
    {
      key: "id",
      header: "Actions",
      width: "w-[30%]",
      render: (choice) => (
        editingIndex === choice.id ? (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={saveEdit}
              className="text-xs"
            >
              Save
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={cancelEdit}
              className="text-xs"
            >
              Cancel
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => startEditing(choice.id)}
              className="gap-1"
            >
              <EditIcon className="w-3 h-3" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRemoveChoice(choice.id)}
              className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
              disabled={choices.length <= 1}
            >
              <TrashIcon className="w-3 h-3" />
            </Button>
          </div>
        )
      )
    }
  ];

  return (
    <PaginatedTable
      data={tableData}
      columns={columns}
      loading={false}
      currentPage={1}
      onPageChange={() => {}} // No pagination needed for this table
      pageSize={tableData.length}
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
      showPagination={false}
      loadingRows={5}
    />
  );
};

export default ChoicesTable; 